import { useNavigate, Link } from "react-router-dom";
import { Brain, ArrowLeft, Shield } from "lucide-react";

function PolicyLayout({ title, icon: Icon, children }) {
  const navigate = useNavigate();
  return (
    <div className="marketing-bg min-h-screen">
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={16} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-[#00F0FF]/10 flex items-center justify-center">
            <Icon size={20} className="text-[#00F0FF]" strokeWidth={1.5} />
          </div>
          <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">ShansoAI Hub</span>
        </div>
        <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-3">{title}</h1>
        <p className="text-[#A1A1AA] text-sm font-dm-sans mb-10">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
        <div className="prose prose-invert max-w-none space-y-8 text-[#C0C0C0] font-dm-sans leading-relaxed">
          {children}
        </div>
      </div>
      <footer className="border-t border-[#27272A] py-6 px-6 mt-10">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <p className="text-[#A1A1AA] text-xs font-dm-sans">© {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Privacy Policy</Link>
            <Link to="/terms" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Terms</Link>
            <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Refund Policy</Link>
            <Link to="/contact" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 p-6">
      <h2 className="font-outfit font-bold text-white text-xl mb-3">{title}</h2>
      <div className="text-[#A1A1AA] text-sm leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  return (
    <PolicyLayout title="Privacy Policy" icon={Shield}>
      <Section title="1. Introduction">
        <p>ShansoAI Hub ("we", "our", or "us") is committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you register for our AI training programs or visit our website.</p>
      </Section>
      <Section title="2. Information We Collect">
        <p>We collect information you provide directly to us when registering for a training, including:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Full name</li>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Payment transaction details (processed securely via Cashfree)</li>
        </ul>
        <p className="mt-2">We do not store full payment card details — all payment processing is handled securely by Cashfree Payment Gateway.</p>
      </Section>
      <Section title="3. How We Use Your Information">
        <ul className="list-disc list-inside space-y-1">
          <li>To process your training registration and payment</li>
          <li>To send training confirmation and updates</li>
          <li>To issue and deliver your completion certificate</li>
          <li>To communicate important announcements about your enrolled training</li>
          <li>To improve our services and training offerings</li>
        </ul>
      </Section>
      <Section title="4. Data Sharing">
        <p>We do not sell, trade, or rent your personal data to third parties. We may share your information with:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li><strong className="text-white">Cashfree Payments</strong> — for payment processing</li>
          <li><strong className="text-white">Email service providers</strong> — for sending certificates and communications</li>
          <li>Law enforcement or regulatory bodies when required by law</li>
        </ul>
      </Section>
      <Section title="5. Data Security">
        <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Data is transmitted using SSL/TLS encryption.</p>
      </Section>
      <Section title="6. Your Rights">
        <p>You have the right to:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Access the personal data we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data (subject to legal obligations)</li>
          <li>Withdraw consent at any time</li>
        </ul>
        <p className="mt-2">To exercise these rights, contact us at <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a></p>
      </Section>
      <Section title="7. Cookies">
        <p>Our website may use cookies to enhance your browsing experience. You can choose to disable cookies through your browser settings, though this may affect certain functionality.</p>
      </Section>
      <Section title="8. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on this page with an updated revision date.</p>
      </Section>
      <Section title="9. Contact Us">
        <p>If you have questions about this Privacy Policy, please contact us at <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a></p>
      </Section>
    </PolicyLayout>
  );
}
